import Tooltip from './Tooltip';
import { tr } from '../lib/i18nHelpers';
import { isTemporaryAccount } from '../lib/coa-hierarchy';
import { entryBalanced } from '../lib/auditTrailHelpers';

export interface JeLine {
  account_code: string;
  account_name: string;
  debit: number;
  credit: number;
  account_type?: string | null;
}

export const money = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Shared Dr/Cr presentation language, used by the AR/AP audit trail, the GJE
 * entry popup, and (in miniature) the review queue. One vocabulary everywhere:
 * blue Dr / orange Cr chips, TEMP tags on suspense lines, a balanced footer.
 */

export type TagKind = 'confirmed' | 'suggested' | 'group' | 'manual' | 'auto' | 'temp' | 'reviewed';

/** Small status tag with hover explanation (legibility first). */
export function Tag({ kind, children }: { kind: TagKind; children?: React.ReactNode }) {
  const styles: Record<string, string> = {
    confirmed: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    suggested: 'bg-yellow-100 text-yellow-900 dark:text-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300',
    group: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    manual: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    auto: 'bg-muted text-muted-foreground',
    temp: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    reviewed: 'bg-amber-100 text-amber-700',
  };
  const tips: Record<string, React.ReactNode> = {
    confirmed: tr('A user reviewed and accepted this bank↔invoice link. Confirming posts the payment to the GL and marks the invoice paid.', '用戶已審核並確認此銀行↔發票連結。確認後過賬付款分錄並標記發票已付。', '用户已审核并确认此银行↔发票连结。确认后過账付款分录并标记发票已付。'),
    suggested: tr('The matching engine proposed this link; it is not yet bookkeeping until a user confirms.', '配對引擎建議的連結，未經確認前不入賬。', '配对引擎建议的连结，未经确认前不入账。'),
    group: tr('One bank transaction settles several invoices together; each invoice gets its share of the amount.', '一筆銀行交易結算多張發票，每張發票獲分配其份額。', '一笔银行交易结算多张发票，每张发票获分配其份额。'),
    manual: tr('A user personally picked or edited the accounts of this posting; automation will not overwrite it.', '用戶手動選擇或修改過此分錄的科目；自動化不會覆寫。', '用户手动选择或修改过此分录的科目；自动化不会覆写。'),
    auto: tr('Generated automatically by the rules when the statement was imported or the match confirmed.', '由系統規則於導入月結單或確認配對時自動產生。', '由系统规则于导入月结单或确认配对时自动产生。'),
    temp: tr('Temporary suspense account — money is parked here until the real story is matched. Should clear to zero once both sides are linked.', '暫記科目——尚未配對真實憑證前的暫存。兩側連結後應歸零。', '暂记科目——尚未配对真实凭证前的暂存。两侧连结后应归零。'),
    reviewed: tr('A user approved this variance with a written reason; recorded in the audit log.', '用戶已書面批准此差異並記錄於審計日誌。', '用户已书面批准此差异并记录于审计日志。'),
  };
  const label: Record<string, string> = {
    confirmed: 'CONFIRMED', suggested: 'SUGGESTED', group: 'GROUP',
    manual: 'MANUAL', auto: 'AUTO', temp: 'TEMP', reviewed: 'REVIEWED',
  };
  return (
    <Tooltip content={tips[kind]}>
      <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full cursor-help ${styles[kind]}`}>
        {children ?? label[kind]}
      </span>
    </Tooltip>
  );
}

/** One Dr/Cr ledger table (bank side or one invoice's entry). */
export function LedgerBlock({ lines, footer }: { lines: JeLine[]; footer?: React.ReactNode }) {
  const balanced = entryBalanced(lines);
  return (
    <table className="w-full text-xs">
      <tbody>
        {lines.map((l, i) => {
          const side = (l.debit || 0) > 0 ? 'dr' : 'cr';
          const temp = isTemporaryAccount(l.account_name);
          return (
            <tr key={i} className="border-t border-muted/40">
              <td className="py-1.5 pr-2 w-10">
                <span className={`inline-block font-mono font-bold text-[10px] px-1.5 py-px rounded ${side === 'dr' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'}`}>
                  {side === 'dr' ? 'Dr' : 'Cr'}
                </span>
              </td>
              <td className="py-1.5 pr-2">
                <span className="font-mono font-semibold">{l.account_code}</span>{' '}
                <span className="text-muted-foreground">{l.account_name}</span>
                {temp && <span className="ml-1.5 align-middle"><Tag kind="temp" /></span>}
              </td>
              <td className="py-1.5 text-right font-mono font-medium whitespace-nowrap">
                {money(l.debit || l.credit)}
              </td>
            </tr>
          );
        })}
        <tr className={`border-t ${balanced ? 'bg-green-50/60 dark:bg-green-950/20' : 'bg-red-50/60'}`}>
          <td colSpan={3} className="py-1 px-2 text-[11px] font-semibold text-center">
            {balanced ? '✓ ' + tr('Balanced — Dr = Cr', '平衡 —— 借 = 貸', '平衡 —— 借 = 贷') : '⚠ ' + tr('UNBALANCED', '不平衡', '不平衡')}
            {footer ? <span className="font-normal text-muted-foreground"> · {footer}</span> : null}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/** Shared hint when the temp account appears on both sides of a pairing. */
export function TempCancelNote({ contraLines, otherLines }: { contraLines: JeLine[]; otherLines: JeLine[] }) {
  const contraCodes = new Set(contraLines.map(l => l.account_code));
  const shared = otherLines.map(l => l.account_code).find(code => contraCodes.has(code));
  if (!shared) return null;
  const name = (contraLines.find(l => l.account_code === shared) || otherLines.find(l => l.account_code === shared))?.account_name || shared;
  if (!isTemporaryAccount(name)) return null;
  return (
    <div className="mt-3 text-[11.5px] bg-blue-50 border border-dashed border-blue-200 text-blue-900 rounded-lg px-3 py-2 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-800">
      <b className="font-mono">{shared} {name}</b>{' '}
      {tr('appears on both sides — the bank payment and the invoice entries cancel out here, so the net book effect is simply bank ↔ revenue. Nothing is left hanging in the temp account.', '同時出現於兩側——銀行付款與發票分錄在此互相抵銷，帳面淨效果即為銀行↔收入。暫記科目不會留有餘額。', '同时出现于两侧——银行付款与发票分录在此互相抵销，帐面净效果即为银行↔收入。暂记科目不会留有余额。')}
    </div>
  );
}
